import {
  countryImgUrl,
  formatNumber,
  getInitialCountryCode,
  isHasContactKey,
  isHasProductKey,
} from "@/00-shared/const";
import {
  CustomizedTimeFilter,
  SearchEntityResult,
} from "@/01-core/entity/entities/entity";
import {
  AtomicFieldType,
  SortOrder,
} from "@/01-core/entity/entities/viewDetail";
import { locales } from "@/i18n";
import {
  parseISO,
  differenceInSeconds,
  format,
  fromUnixTime,
  addDays,
  differenceInDays,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";
import crypto from "crypto";
import { CurrencyField, EntityV2 } from "@/01-core/entity/entities/entityV2";
import { isNil } from "lodash";
import { FieldType } from "./type";

export function calculateTimeDifference(start: string, end: string): string {
  if (!start || !end) {
    return "";
  }
  const startDate = parseISO(start);
  const endDate = parseISO(end);
  const diffInSeconds = differenceInSeconds(endDate, startDate);
  const hours = Math.floor(diffInSeconds / 3600);
  const minutes = Math.floor((diffInSeconds % 3600) / 60);
  const seconds = diffInSeconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}
export function showOnlyMinute(value: string) {
  return format(new Date(value), "MM-dd HH:mm");
}
export const SPLIT_SYMBOL = "--split-from-here--";

export const getFieldByKey = (
  key: string,
  locale: locales,
  results: SearchEntityResult,
  isGetID?: boolean,
  imgPreferThumb: boolean = true,
  getValue: boolean = false,
) => {
  const result = results.entity_list?.[0];
  if (!result || !result.customized_field_list) {
    console.warn("results invalid:", results);
    return null;
  }
  if (isGetID) {
    return result.entity_id;
  }
  const targetField = result.customized_field_list.find(
    (cf) => cf.field_key === key,
  );
  if (!targetField || !targetField.entity_field_type) {
    console.warn("key not found:", key);
    return null;
  }
  // 平铺类型
  if ("atomic_field_type" in targetField.entity_field_type) {
    const fallback = "-";
    if (!targetField.atomic_field) {
      return fallback;
    }

    // text
    const isText =
      targetField.entity_field_type.atomic_field_type === AtomicFieldType.TEXT;
    if (isText && "text" in targetField.atomic_field) {
      return targetField.atomic_field.text?.value ?? fallback;
    }

    // entity_id
    const isID =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.ENTITY_ID;
    if (isID && "entity_id" in targetField.atomic_field) {
      return targetField.atomic_field.entity_id?.value ?? fallback;
    }

    // timestamp
    const isTimestamp =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.TIMESTAMP;
    if (isTimestamp && "timestamp" in targetField.atomic_field) {
      return targetField.atomic_field.timestamp?.value_str ?? fallback;
    }

    // region
    const isRegion =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.COUNTRY;
    if (isRegion && "country" in targetField.atomic_field) {
      const countryCode = targetField.atomic_field.country?.country_code;
      const countryFullName =
        targetField.atomic_field.country?.country_full_name[locale];
      if (!countryFullName) return fallback;
      // hardcode: 原本这里是返回emoji + 国家名称，但Windows不支持，故改成静态图片地址
      // 为了函数返回值都是字符串，这里用一个特殊字符串进行拼接
      return `${countryImgUrl(
        countryCode ?? "",
      )}${SPLIT_SYMBOL}${countryFullName}`;
    }
    // currency
    const isCurrency =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.CURRENCY;
    if (isCurrency && "currency" in targetField.atomic_field) {
      return (
        targetField.atomic_field.currency?.rendered_value_str[locale] ?? ""
      );
    }

    // number
    const isNumber =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.NUMBER;
    if (isNumber && "number" in targetField.atomic_field) {
      return targetField.atomic_field.number?.value_str[locale] ?? "-";
    }

    // url
    const isUrl =
      targetField.entity_field_type.atomic_field_type === AtomicFieldType.URL;
    if (isUrl && "url" in targetField.atomic_field) {
      return targetField.atomic_field.url?.url;
    }

    // record url
    const isRecordUrl =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.Record_URL;
    if (isRecordUrl && "live_recording" in targetField.atomic_field) {
      return targetField.atomic_field.live_recording?.url;
    }

    // review tags
    const isReviewTags =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.REVIEW_TAGS;
    if (isReviewTags && "review_tags" in targetField.atomic_field) {
      return targetField.atomic_field.review_tags?.value;
    }

    const isReviewOpinion =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.REVIEWOPINION;
    if (isReviewOpinion && "review_opinions" in targetField.atomic_field) {
      return targetField.atomic_field.review_opinions?.value;
    }

    const isPartneredBrand =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.PARTNERED_BRAND;
    if (isPartneredBrand && "partner_brand" in targetField.atomic_field) {
      return targetField.atomic_field.partner_brand?.value;
    }

    // cascader
    const isCascader =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.CASCADER;
    if (isCascader && "cascader" in targetField.atomic_field) {
      const cascader = targetField.atomic_field.cascader?.value;
      if (!cascader || !cascader.length) {
        return fallback;
      }
      return cascader
        .filter((c) => !!c)
        .map((c) => c.value[locale])
        .join(" / ");
    }

    // image
    const isImage =
      targetField.entity_field_type.atomic_field_type === AtomicFieldType.IMAGE;
    if (isImage && "image" in targetField.atomic_field) {
      if (imgPreferThumb) {
        return (
          targetField.atomic_field.image?.thumb_url_list?.[0] ??
          targetField.atomic_field.image?.url_list?.[0]
        );
      } else {
        return targetField.atomic_field.image?.url_list?.[0];
      }
    }

    // option
    const isOption =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.OPTION;
    if (isOption) {
      if ("option" in targetField.atomic_field) {
        return targetField.atomic_field.option.value[locale] ?? "-";
      } else {
        return "-";
      }
    }

    // condition number
    const isConditionNumber =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.CONDITION_NUMBER;
    if (isConditionNumber) {
      if ("condition_number" in targetField.atomic_field) {
        return getValue
          ? Number(targetField.atomic_field.condition_number.value)
          : targetField.atomic_field.condition_number.value_str[locale] ?? "-";
      } else {
        return "-";
      }
    }

    // enumerator
    const isEnumerator =
      targetField.entity_field_type.atomic_field_type ===
      AtomicFieldType.ENUMERATOR;
    if (isEnumerator) {
      if ("enumerator" in targetField.atomic_field) {
        return getValue
          ? targetField.atomic_field.enumerator?.value
          : targetField.atomic_field.enumerator?.display_name?.[locale] ?? "-";
      }
    }
  }
  // 数组类型
  if ("array" in targetField.entity_field_type) {
    const fallback: string[] = [];
    if (!targetField.array) {
      return fallback;
    }
    // images
    const isImage =
      targetField.entity_field_type.array.atomic_field ===
      AtomicFieldType.IMAGE;
    if (isImage) {
      return targetField.array
        .map((item) => {
          if ("image" in item) {
            if (imgPreferThumb) {
              return (
                item.image?.thumb_url_list?.[0] ?? item.image?.url_list?.[0]
              );
            } else {
              return item.image?.url_list?.[0];
            }
          }
        })
        .filter((url) => !!url);
    }

    // text
    const isText =
      targetField.entity_field_type.array.atomic_field === AtomicFieldType.TEXT;
    if (isText) {
      return targetField.array
        .map((item) => {
          if ("text" in item) {
            return item.text?.value;
          }
        })
        .filter((val) => !!val)
        .join(", ");
    }

    // json
    const isJson =
      targetField.entity_field_type.array.atomic_field ===
      AtomicFieldType.JSON_;
    if (isJson) {
      return targetField.array.map((f: any) => f?.json_?.value);
    }

    // option
    const isOption =
      targetField.entity_field_type.array.atomic_field ===
      AtomicFieldType.OPTION;
    if (isOption) {
      if (getValue) {
        return targetField.array.map(
          (item: any) => item?.option?.value?.[locale],
        );
      } else {
        return targetField.array
          .map((item: any) => item?.option?.value?.[locale])
          .join("、");
      }
    }
  }
  return null;
};
const shouldShowRankList = [
  "product_sold_count_view",
  "shop_sold_count_view",
  "product_promotion_view",
  "shop_most_promoted_view",
  "creator_fans_growth_view",
  "creator_ecommerce_creators_view",
];
export function shouldShowRank(
  view_name: string,
  sort: { field: string; sort_order: SortOrder }[] | null,
) {
  if (!shouldShowRankList.includes(view_name)) {
    return false;
  }
  if (!sort?.[0]) {
    return false;
  }
  return sort[0].sort_order === SortOrder.DESC;
}

export function extractNumber(input: string): number | null {
  const regex = /(-?\d+\.?\d*)/;
  const match = input.match(regex);
  if (match) {
    const numStr = match[1];
    return parseFloat(numStr);
  }
  return null;
}
export function calculateActualAmount(amountString: string): number {
  // 匹配数字、小数点和单位
  const match = amountString?.match(/^\$?(\d+(\.\d+)?)((万|亿))?$/);

  if (!match) {
    return 0;
  }

  const [, amountStr, , unit] = match;
  let amount = parseFloat(amountStr);

  switch (unit) {
    case "万":
      amount *= 10000;
      break;
    case "亿":
      amount *= 100000000;
      break;
  }

  return amount;
}

export function isVideoEntity(entityTypeName: string) {
  return ["tiktok_video", "tiktok_ads"].includes(entityTypeName);
}

export function hashEmailToRandomDigit(email: string) {
  // 使用SHA-256算法生成哈希值
  const hash = crypto.createHash("sha256").update(email).digest("hex");

  // 将哈希值的每个字符的字符码相加
  let sum = 0;
  for (let i = 0; i < hash.length; i++) {
    sum += hash.charCodeAt(i);
  }

  // 将字符码的总和映射到0-9的范围内
  const randomDigit = sum % 10;

  return randomDigit + 1;
}
export const filterKeyMap: Record<string, string> = {
  tiktok_product: "product_id",
  tiktok_seller: "seller_id",
  tiktok_creator: "creator_id",
  tiktok_ads: "ad_creative_id",
  tiktok_video: "video_id",
  tiktok_live: "room_id",
};
export function formatTime(value: number, formatString: string = "yyyy-MM-dd") {
  try {
    const utcDate = fromUnixTime(value);

    // 转换为东八区（北京）时间
    const beijingDate = toZonedTime(utcDate, "Asia/Shanghai");

    // 格式化日期
    return format(beijingDate, formatString);
  } catch {
    return value?.toString();
  }
}
export function formatMsTime(
  value: number | undefined | null,
  formatString: string = "yyyy-MM-dd",
) {
  if (isNil(value)) {
    return "-";
  }
  try {
    const utcDate = fromUnixTime(value / 1000);

    // 转换为东八区（北京）时间
    const beijingDate = toZonedTime(utcDate, "Asia/Shanghai");

    // 格式化日期
    return format(beijingDate, formatString);
  } catch {
    return value?.toString();
  }
}
export const isHasContactOrhasProduct = (key: string) => {
  return [isHasContactKey, isHasProductKey].includes(key);
};
export const handleTimeFilter = (timeFilter: CustomizedTimeFilter) => {
  const res: CustomizedTimeFilter = [];
  timeFilter.forEach((filter) => {
    const { start_date_time, end_date_time } = filter;
    if (start_date_time && end_date_time && start_date_time === end_date_time) {
      res.push({
        ...filter,
        end_date_time: format(addDays(parseISO(end_date_time), 1), "yyyyMMdd"),
      });
    } else {
      res.push(filter);
    }
  });
  return res;
};
export function formatToLocalTime(
  num: number,
  formatString: string = "yyyy-MM-dd HH:mm:ss",
): string {
  try {
    const date = fromUnixTime(num);
    return format(date, formatString);
  } catch (error) {
    return num + "";
  }
}
export function getLastDay(expairedTime: number) {
  try {
    return differenceInDays(fromUnixTime(expairedTime), new Date());
  } catch {
    return null;
  }
}
export function getValueByKeyV2(
  entity: EntityV2,
  key: string,
): any | undefined {
  const field = entity?.field_list?.find((f) => f.field_key === key);
  if (field) {
    return field.value;
  }
  return undefined;
}

export function getCurrencySymbol(unit?: string) {
  switch (unit) {
    case "US":
      return "$";
    case "TH":
      return "฿";
    case "VN":
      return "₫";
    case "MY":
      return "RM";
    case "PH":
      return "₱";
    case "CNY":
      return "¥";
    case "MX":
      return "M$";
    case "DE":
    case "FR":
    case "IT":
    case "ES":
      return "€";
    case "JP":
      return "JP¥";
    case "BR":
      return "R$";
    default:
      return "$";
  }
}
export const strNumToPercentage = (commissionRate: string) => {
  const toNum = Number(commissionRate);
  if (isNaN(toNum)) {
    return commissionRate;
  }
  return `${Math.floor(toNum * 100)}%`;
};

export function formatCurrency(
  value?: CurrencyField["value"] | null,
  countryCode?: string,
) {
  if (isNil(value?.value)) {
    return `${getCurrencySymbol(value?.unit || countryCode)}0.00`;
  }
  return `${getCurrencySymbol(value?.unit || countryCode)}${formatNumber(
    value?.value,
    2,
    true,
  )}`;
}
export function formatPercentage(value?: number) {
  if (isNil(value)) {
    return "-";
  }
  return `${(value * 100).toFixed(2)}%`;
}
export function formatThousand(value?: number) {
  if (isNil(value)) {
    return "-";
  }
  return `${(value * 1000).toFixed(2)}‰`;
}

export function convertToMinutes(input: number): string {
  try {
    const date = new Date(0, 0, 0, 0, 0, input / 1000);
    return format(date, "mm:ss");
  } catch (e) {
    return "-";
  }
}
export function convertSecondsToMinutes(input: number): string {
  try {
    const date = new Date(0, 0, 0, 0, 0, input);
    return format(date, "mm:ss");
  } catch (e) {
    return "-";
  }
}
export function downloadFileFromURL(
  url: string,
  fileName: string,
  suffix?: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url);
    xhr.responseType = "arraybuffer";

    xhr.onload = function () {
      try {
        // 从响应头获取content-type，如果没有则默认为application/octet-stream
        const contentType =
          xhr.getResponseHeader("content-type") || "application/octet-stream";
        const blob = new Blob([xhr.response], { type: contentType });
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        const finalFileName = suffix ? `${fileName}${suffix}` : fileName;
        link.download = finalFileName;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 清理blob URL
        URL.revokeObjectURL(blobUrl);
        resolve();
      } catch (error) {
        reject(error);
      }
    };

    xhr.onerror = () => {
      reject(new Error("Download failed"));
    };

    xhr.send();
  });
}

export const formatPrice = (
  floorPriceObject?: CurrencyField["value"],
  ceilingPriceObject?: CurrencyField["value"],
) => {
  const floorPrice = floorPriceObject?.value;
  const ceilingPrice = ceilingPriceObject?.value;
  const codeSymbol = getCurrencySymbol(floorPriceObject?.unit);
  if (floorPrice && ceilingPrice) {
    if (floorPrice === ceilingPrice) {
      return `${codeSymbol}${floorPrice}`;
    } else {
      return `${codeSymbol}${floorPrice}-${codeSymbol}${ceilingPrice}`;
    }
  }
  if (floorPrice && !ceilingPrice) {
    return `${codeSymbol}${floorPrice}`;
  }
  if (!floorPrice && ceilingPrice) {
    return `${codeSymbol}${ceilingPrice}`;
  }
  return "-";
};

/**
 * 将对象转换为查询字符串
 * - 例如：{ a: 1, b: 2 } => "a=1&b=2"
 */
export const objToQuery = (params?: Record<string, any>) => {
  if (!params) return "";

  const searchParams = new URLSearchParams(params);
  return searchParams.toString();
};

/**
 * 格式化视频时长
 * - 例如：10000 => "10秒"
 */
export const formatVideoDuration = (duration?: number) => {
  return duration ? `${Math.round(duration / 1000)} 秒` : "";
};

export function formatField(type: FieldType, value: any) {
  switch (type) {
    case FieldType.NUMBER:
      return formatNumber(value) ?? 0;

    case FieldType.CURRENCY:
      return formatCurrency(value) ?? "--";

    case FieldType.DURATION:
      return `时长: ${formatVideoDuration(value)}`;

    case FieldType.IMAGE:
      return value?.url;

    case FieldType.IMAGE_LIST:
      return value?.[0]?.url;

    case FieldType.PRICE:
      return formatPrice(value?.floorPrice, value?.ceilingPrice) ?? "--";

    case FieldType.PERCENTAGE:
      return formatPercentage(value);

    case FieldType.FLOAT:
      return formatNumber(value, 2, true);

    case FieldType.CONVERSION_RATE:
      return formatThousand(value);

    case FieldType.NUMBER_CURRENCY:
      return formatCurrency({ value, unit: "" }, getInitialCountryCode());

    default:
      return value;
  }
}

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return ["chuhaijiang", "biyikuajing", "tiktok", "amazon"].some((item) =>
      url.includes(item),
    );
  } catch (_) {
    return false;
  }
}

export const durationMoreThan5Min = (duration: number) =>
  duration > 5 * 60 * 1000;

export function generateHighlightText(text: string, keywords: string[]) {
  const result: {
    content: string;
    isHighilighted: boolean;
  }[] = [];
  if (keywords.length === 0) {
    return [{ content: text, isHighilighted: false }];
  }
  const sortedKeywords = keywords.sort((a, b) => b.length - a.length);
  const regex = new RegExp(sortedKeywords.join("|"), "gi");
  let match: RegExpExecArray | null;
  let lastIndex = 0;
  try {
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push({
          content: text.slice(lastIndex, match.index),
          isHighilighted: false,
        });
      }
      result.push({
        content: match[0],
        isHighilighted: true,
      });
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      result.push({
        isHighilighted: false,
        content: text.slice(lastIndex),
      });
    }

    return result;
  } catch {
    return null;
  }
}

export function getThHeight(numbers: number[]): number[] {
  if (!numbers || numbers.length === 0) return [];

  // 复制原数组
  let result = [...numbers];
  const sum = numbers.reduce((acc, curr) => acc + curr, 0);
  const avg = sum / numbers.length;

  // 找出小于平均值的数字
  const smallNumbers = result
    .map((num, index) => ({ num, index }))
    .filter((item) => item.num < avg);

  // 找出大于平均值的数字
  const largeNumbers = result
    .map((num, index) => ({ num, index }))
    .filter((item) => item.num > avg);

  if (smallNumbers.length === 0 || largeNumbers.length === 0) {
    return result;
  }

  // 从大数中按比例分配
  const totalExcess = largeNumbers.reduce(
    (acc, { num }) => acc + (num - avg),
    0,
  );

  // 对每个小数进行调整
  for (const { index } of smallNumbers) {
    const increment = avg - result[index];
    if (increment <= 0) continue;

    // 从大数中按比例减少
    let remainingIncrement = increment;
    for (const { index: largeIndex, num } of largeNumbers) {
      if (remainingIncrement <= 0) break;

      const proportion = (num - avg) / totalExcess;
      const decrease = Math.floor(remainingIncrement * proportion);

      if (decrease > 0 && result[largeIndex] > avg) {
        const actualDecrease = Math.min(decrease, result[largeIndex] - avg);
        result[largeIndex] -= actualDecrease;
        result[index] += actualDecrease;
        remainingIncrement -= actualDecrease;
      }
    }
  }

  return result;
}
export function getVideoCtrOrCvr(value: number | undefined | null) {
  if (isNil(value)) {
    return "-";
  }
  if (value === 1) {
    return "低";
  }
  if (value === 2) {
    return "中";
  }
  if (value === 3) {
    return "高";
  }
  return "-";
}

export function getCreatorType(type: number) {
  const typeMap: Record<number, string> = {
    0: "个人账号",
    1: "专业账号",
    2: "达人号",
    3: "店铺运营号",
    4: "店铺绑定号",
  };
  return typeMap[type] ?? "-";
}
export function getLast3MonthRange(month: number) {
  if (month > 10) {
    return `去年 ${month} 月至今年 ${month - 10} 月`;
  }
  return `去年 ${month} 月至 ${month + 2} 月`;
}

export function getBlobUrl(
  url: string,
  isIOSOrSafari: boolean,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!isIOSOrSafari) {
      resolve(url);
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url);
    xhr.responseType = "arraybuffer";

    xhr.onload = function () {
      try {
        // 从响应头获取content-type，如果没有则默认为application/octet-stream
        const contentType =
          xhr.getResponseHeader("content-type") || "application/octet-stream";
        const blob = new Blob([xhr.response], { type: contentType });
        const blobUrl = URL.createObjectURL(blob);
        resolve(blobUrl);
      } catch (error) {
        console.error(error);
        resolve(url);
      }
    };

    xhr.onerror = () => {
      console.error("Download failed");
      resolve(url);
    };

    xhr.send();
  });
}
